import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { saveFormDetails } from "../../redux/slices/CreateAgentSlice";
import { FORM_KEYS } from "../../utills/constant";
import { apiUploadFiles, apiGet } from "../../api/apiclient";
import { UPLOAD_FILES } from "../../api/endpoints";

// Custom hooks
import { useFileUpload } from './hooks/useFileUpload';
import { useDragAndDrop } from './hooks/useDragAndDrop';

// Components
import UploadArea from './UploadArea';
import SelectedFilesList from './SelectedFilesList';
import UploadButton from './UploadButton';
import UploadProgressList from './UploadProgressList';
import FolderSelectionDialog from './FolderSelectionDialog';
import SuccessPopup from '../SuccessPopup';

// Styles
import './DocumentUpload.css';

const DocumentUpload = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  
  const folderInputRef = useRef(null);

  const setFormDetailsValue = useCallback((key, value) => {
    dispatch(saveFormDetails({ key, value }));
  }, [dispatch]);

  const {
    uploadedFiles,
    uploading,
    uploadProgress,
    error,
    addFiles,
    removeFile,
    setError,
    setUploading,
    setUploadedFiles
  } = useFileUpload();

  const handleFilesAdded = useCallback((files) => {
    addFiles(files);
  }, [addFiles]);

  const { dragActive, handleDrag, handleDrop } = useDragAndDrop(handleFilesAdded);

  const handleFolderSelect = useCallback(() => {
    setShowFolderDialog(true);
  }, []);

  const handleFolderInputChange = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      const allFiles = Array.from(e.target.files);
      
      const pdfFiles = allFiles.filter(file => {
        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        return isPdf;
      });
      
      if (pdfFiles.length === 0) {
        setError('No PDF files found in the selected folder');
        return;
      }
      
      handleFilesAdded(pdfFiles);
      setShowFolderDialog(false);
    }
  }, [handleFilesAdded, setError]);

  const handleFolderBrowse = useCallback(() => {
    folderInputRef.current?.click();
  }, []);

  // Test API connectivity
  const testApiConnection = useCallback(async () => {
    try {
      console.log('Testing API connection...');
      const response = await apiGet('ai_agents/');
      console.log('API connection test successful:', response);
      return true;
    } catch (error) {
      console.error('API connection test failed:', error);
      return false;
    }
  }, []);



  const handleUpload = useCallback(async () => {
    const pendingFiles = uploadedFiles.filter(f => f.status === 'pending');
    
    if (pendingFiles.length === 0) {
      setError('No files to upload');
      return;
    }
    
    if (pendingFiles.length > 0) {
      try {
        // Test API connection first
        const apiConnected = await testApiConnection();
        if (!apiConnected) {
          setError('Cannot connect to server. Please check your internet connection.');
          return;
        }
        
        // Set uploading state
        setUploading(true);
        
        // Update file status to uploading
        setUploadedFiles(prev => prev.map(f => 
          f.status === 'pending' ? { ...f, status: 'uploading' } : f
        ));
        
        // Call the API to upload files
        const response = await apiUploadFiles(UPLOAD_FILES, pendingFiles);
        
        console.log('Upload response:', response);
        
        // Check if the upload was successful
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          console.log('Upload successful:', response.data);
          // Show success popup
          setError('');
          setShowSuccessPopup(true);
        } else {
          console.log('Upload response received but status unclear:', response.data);
        }
        
        // Update file status to completed
        setUploadedFiles(prev => prev.map(f => 
          f.status === 'uploading' ? { ...f, status: 'completed', progress: 100 } : f
        ));
        
        // Save to form details when upload completes (only metadata, not file objects)
        setFormDetailsValue(FORM_KEYS.INPUT_DIRECTORY_ID, 'document-upload');
        setFormDetailsValue(FORM_KEYS.UPLOADED_FILES_METADATA, 
          uploadedFiles.map(f => ({ 
            name: f.name, 
            size: f.size,
            id: f.id,
            status: f.status
          }))
        );
        
        // Show success message
        setError('');
        // You could also show a success toast here if you have toast notifications
        console.log('Files uploaded successfully!');
        
      } catch (error) {
        console.error('Upload error:', error);
        console.error('Error response:', error.response);
        console.error('Error message:', error.message);
        
        // Update file status to error
        setUploadedFiles(prev => prev.map(f => 
          f.status === 'uploading' ? { ...f, status: 'error' } : f
        ));
        
        // Show more specific error message
        let errorMessage = 'Failed to upload files. Please try again.';
        if (error.response?.status === 500) {
          errorMessage = 'Server error (500). Please try again later.';
        } else if (error.response?.status === 413) {
          errorMessage = 'File too large. Please check file size limits.';
        } else if (error.response?.status === 415) {
          errorMessage = 'Unsupported file type. Please upload PDF files only.';
        } else if (error.response?.status === 400) {
          console.log('400 Error response data:', error.response.data);
          errorMessage = error.response.data?.message || 'Bad request. Please check your input.';
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        setError(errorMessage);
      } finally {
        setUploading(false);
      }
    }
  }, [uploadedFiles, setFormDetailsValue, setUploading, setError, testApiConnection]);

  const pendingFiles = uploadedFiles.filter(f => f.status === 'pending');
  const uploadingFiles = uploadedFiles.filter(f => f.status === 'uploading' || f.status === 'completed');

  return (
    <Box className="document-upload-container">
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography 
          variant="h5" 
          sx={{
            color: '#1F2937',
            fontWeight: 700,
            mb: 1,
            fontSize: '1.5rem',
            letterSpacing: '-0.025em',
            background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Document Upload
        </Typography>
        
        <Typography 
          variant="body2" 
          sx={{
            color: '#6B7280',
            fontSize: '0.875rem',
            lineHeight: 1.5,
            maxWidth: '500px',
            mx: 'auto',
            opacity: 0.8
          }}
        >
          Upload PDF documents for classification. You can drag & drop files or select a binder containing PDFs.
        </Typography>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{
            mb: 3,
            borderRadius: '12px',
            fontSize: '0.875rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            '& .MuiAlert-message': {
              fontSize: '0.875rem'
            }
          }}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <UploadArea
          dragActive={dragActive}
          handleDrag={handleDrag}
          handleDrop={handleDrop}
          onFolderSelect={handleFolderSelect}
        />
      </Box>

      {pendingFiles.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <SelectedFilesList
            files={pendingFiles}
            onRemoveFile={removeFile}
          />
        </Box>
      )}

      {pendingFiles.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <UploadButton
            onUpload={handleUpload}
            files={uploadedFiles}
            uploading={uploading}
          />
        </Box>
      )}

      {uploadingFiles.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <UploadProgressList
            files={uploadingFiles}
            uploadProgress={uploadProgress}
            uploading={uploading}
            onRemoveFile={removeFile}
          />
        </Box>
      )}

      {/* Hidden folder input */}
      <input
        ref={folderInputRef}
        type="file"
        webkitdirectory=""
        directory=""
        multiple
        onChange={handleFolderInputChange}
        style={{ display: 'none' }}
      />

      <FolderSelectionDialog
        open={showFolderDialog}
        onClose={() => setShowFolderDialog(false)}
        onFolderSelect={handleFolderBrowse}
      />

      <SuccessPopup
        open={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        onButtonClick={() => navigate('/agentSelection')}
        title="Files Uploaded Successfully!"
        message="Your documents have been uploaded and are ready for classification."
        buttonText="Thank You!"
      />
    </Box>
  );
};

export default DocumentUpload;
